import { useEffect, useMemo, useRef, useState } from "react";
import type { MRT_PaginationState, MRT_SortingState } from "material-react-table";

import { mrtSortingStateToObject } from "src/api/apiHelpers";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { useGetAllIngredientReferencesUnified } from "src/api/client-api/ingredientReferenceUnified/useGetAllIngredientReferencesUnified";
import type {
    IngredientReferenceSource,
    IngredientReferenceUnifiedScore,
} from "src/api/client-api/ingredientReferenceUnified/types";
import { useGetUnifiedIngredientList } from "src/api/client-api/ingredients/useGetUnifiedIngredientList";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import type { ProductFormProductIngredient } from "src/pages/Product/ProductForm/ProductForm.types";

export type IngredientPickerEntity = {
    "@id": string;
    id: number;
    name: string;
    type: "ingredient" | "intermediate-product" | "ingredient-reference";
    clientId?: string | null;
    code?: string | null;
    cpcCode?: string | null;
    cpcLabel?: string | null;
    origin?: string | null;
    ingredientLine?: { name: string } | null;
    compositeId?: string;
    source?: IngredientReferenceSource;
    defaultOrigin?: string | null;
    fresh?: boolean | null;
    nonEdiblePartRatio?: number | null;
    rawToCookedRatio?: number | null;
    scores?: IngredientReferenceUnifiedScore[];
};

export type IngredientPickerSource = "raw-material" | "intermediate-product";

export const getProductIngredientKey = (entity: ProductFormProductIngredient): string => {
    if (entity.ingredient) {
        return entity.ingredient["@id"];
    }

    if (entity.intermediateProduct) {
        return entity.intermediateProduct["@id"];
    }

    if (entity.ingredientReference) {
        return entity.ingredientReference.compositeId;
    }

    return "";
};

export const getIngredientPickerEntityKey = (entity: IngredientPickerEntity): string => {
    return entity.type === "ingredient-reference" ? (entity.compositeId ?? "") : entity["@id"];
};

export const toProductIngredient = (entity: IngredientPickerEntity): ProductFormProductIngredient => {
    if (entity.type === "ingredient") {
        return {
            ingredient: {
                "@id": entity["@id"],
                id: entity.id,
                name: entity.name,
                origin: entity.origin ?? null,
                suppliersName: [],
            },
            quantity: null,
            usedQuantity: null,
        };
    }

    if (entity.type === "intermediate-product") {
        return {
            intermediateProduct: {
                "@id": entity["@id"],
                id: entity.id,
                title: entity.name,
            },
            quantity: null,
            usedQuantity: null,
        };
    }

    if (!entity.compositeId || !entity.source) {
        throw new Error("Ingredient reference entity must include a composite id and a source");
    }

    return {
        ingredientReference: {
            compositeId: entity.compositeId,
            id: entity.id,
            source: entity.source,
            name: entity.name,
            defaultOrigin: entity.defaultOrigin,
            fresh: entity.fresh,
            nonEdiblePartRatio: entity.nonEdiblePartRatio,
            rawToCookedRatio: entity.rawToCookedRatio,
        },
        quantity: null,
        usedQuantity: null,
        origin: entity.defaultOrigin ?? null,
    };
};

type UseIngredientPickerRowsOptions = {
    delayedSearch: string;
    source: IngredientPickerSource;
};

export const useIngredientPickerRows = (options: UseIngredientPickerRowsOptions) => {
    const { delayedSearch, source } = options;
    const { selectedCompany } = useFoodpilotContext();
    const { isFeatureActivated } = useApplicationParameters();
    const areIngredientsEnabled = isFeatureActivated("FS-entity-ingredients");
    const areManagedIngredientsEnabled = isFeatureActivated("FS-manage-my-ingredients");
    const shouldUseManagedIngredients =
        source === "raw-material" && areIngredientsEnabled && areManagedIngredientsEnabled;
    const shouldUseIntermediateProducts =
        source === "intermediate-product" && isFeatureActivated("FS-entity-intermediate-products");
    const shouldUseUnifiedIngredients = shouldUseManagedIngredients || shouldUseIntermediateProducts;
    const shouldUseIngredientReferences =
        areIngredientsEnabled && !shouldUseManagedIngredients && source === "raw-material";

    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 6,
    });
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "name", desc: false }]);
    const queryIdentity = `${source}\u0000${delayedSearch}`;
    const previousQueryIdentity = useRef(queryIdentity);
    const queryIdentityChanged = previousQueryIdentity.current !== queryIdentity;
    const effectivePagination =
        queryIdentityChanged && pagination.pageIndex !== 0 ? { ...pagination, pageIndex: 0 } : pagination;

    useEffect(() => {
        previousQueryIdentity.current = queryIdentity;
        setPagination((currentPagination) =>
            currentPagination.pageIndex === 0 ? currentPagination : { ...currentPagination, pageIndex: 0 },
        );
    }, [queryIdentity]);

    const { unifiedIngredients, isLoading, totalItems } = useGetUnifiedIngredientList({
        cacheTime: 0,
        enabled: shouldUseUnifiedIngredients,
        params: {
            page: effectivePagination.pageIndex + 1,
            itemsPerPage: effectivePagination.pageSize,
            organization_id: selectedCompany?.id,
            type: source === "raw-material" ? "ingredient" : "intermediate-product",
            ...mrtSortingStateToObject(sorting),
        },
        search: delayedSearch,
    });

    const {
        ingredientReferences,
        isLoading: areIngredientReferencesLoading,
        totalItems: totalIngredientReferences,
    } = useGetAllIngredientReferencesUnified({
        q: delayedSearch,
        source: "all",
        page: effectivePagination.pageIndex + 1,
        itemsPerPage: effectivePagination.pageSize,
        enabled: shouldUseIngredientReferences,
        cacheTime: 0,
        staleTime: 0,
    });

    const rows: IngredientPickerEntity[] = useMemo(() => {
        if (shouldUseIngredientReferences) {
            return (ingredientReferences ?? []).map((item) => ({
                "@id": item.compositeId,
                id: item.id,
                name: item.name,
                type: "ingredient-reference",
                code: item.code,
                cpcCode: item.cpcCode,
                cpcLabel: item.cpcLabel,
                origin: item.defaultOrigin,
                compositeId: item.compositeId,
                source: item.source,
                defaultOrigin: item.defaultOrigin,
                fresh: item.fresh,
                nonEdiblePartRatio: item.nonEdiblePartRatio,
                rawToCookedRatio: item.rawToCookedRatio,
                scores: item.scores,
            }));
        }

        if (!shouldUseUnifiedIngredients) {
            return [];
        }

        return (unifiedIngredients ?? []).map((item) => ({
            "@id": item["@id"],
            id: item.id,
            name: item.name,
            type: item.type,
            clientId: item.clientId,
            origin: item.origin,
            ingredientLine: item.ingredientLine,
            scores: item.scores,
        }));
    }, [ingredientReferences, shouldUseIngredientReferences, shouldUseUnifiedIngredients, unifiedIngredients]);

    return {
        rows,
        isLoading:
            shouldUseUnifiedIngredients ? isLoading
            : shouldUseIngredientReferences ? areIngredientReferencesLoading
            : false,
        totalItems:
            shouldUseUnifiedIngredients ? (totalItems ?? 0)
            : shouldUseIngredientReferences ? (totalIngredientReferences ?? 0)
            : 0,
        pagination: effectivePagination,
        setPagination,
        sorting,
        setSorting,
    };
};
